import httpMul from "@/utils/httpMul";
import { expiredStorage } from "@/utils/expiredStorage";
export class StartExploreApi {
    /**
     * 通知数据保存已完成（由外部调用，通常在ok函数中调用）
     */
    static notifyDataSaved() {
        if (this.dataSaveResolver) {
            console.log('✅ 收到数据保存完成通知');
            this.dataSaveResolver();
            this.dataSaveResolver = null;
            this.dataSavePromise = null;
        }
    }
    /**
     * 通知数据保存失败（由外部调用）
     */
    static notifyDataSaveFailed(error) {
        if (this.dataSavePromise) {
            console.error('❌ 数据保存失败:', error);
            // 如果Promise还在等待，reject它
            // 但这里我们使用resolve，让等待继续执行（避免阻塞）
            if (this.dataSaveResolver) {
                this.dataSaveResolver();
                this.dataSaveResolver = null;
            }
            this.dataSavePromise = null;
        }
    }
    /**
     * 初始化数据保存Promise（在startExplore调用前调用）
     */
    static initDataSavePromise() {
        // 如果已有Promise在等待，先清除
        if (this.dataSavePromise) {
            console.warn('已有数据保存Promise在等待，清除旧的');
        }
        // 创建新的Promise
        this.dataSavePromise = new Promise((resolve) => {
            this.dataSaveResolver = resolve;
        });
        console.log('初始化数据保存Promise');
    }
    /**
     * 等待当前正在进行的startExplore请求完成，并确保数据已保存到存储
     * @returns Promise<void> 如果当前没有请求，立即resolve
     */
    static async waitForCurrentRequest() {
        if (this.currentRequestPromise) {
            try {
                // 等待API调用完成
                await this.currentRequestPromise;
                console.log('startExplore API调用已完成，等待数据保存...');
                // 等待数据保存完成
                if (this.dataSavePromise) {
                    await this.dataSavePromise;
                    console.log('✅ 数据保存已完成，可以继续执行');
                }
                else {
                    console.warn('⚠️ 没有数据保存Promise，可能数据保存逻辑未正确初始化');
                    // 如果没有Promise，使用轮询作为后备方案
                    await this.pollForDataSave();
                }
            }
            catch (error) {
                // 忽略错误，只等待完成
                console.log('等待startExplore请求完成（可能失败）:', error);
                // 即使API失败，也尝试等待数据保存
                if (this.dataSavePromise) {
                    try {
                        await this.dataSavePromise;
                    }
                    catch (e) {
                        console.log('等待数据保存时出错:', e);
                    }
                }
            }
        }
    }
    /**
     * 轮询检查数据是否已保存（后备方案）
     */
    static async pollForDataSave() {
        let retryCount = 0;
        const maxRetries = 100; // 最多等待10秒 (100 * 100ms)
        const checkInterval = 100; // 每100ms检查一次
        while (retryCount < maxRetries) {
            const userData = expiredStorage.getItem('user');
            if (userData && userData.face_image_url) {
                console.log('✅ 通过轮询检测到数据已保存，face_image_url:', userData.face_image_url);
                return;
            }
            await new Promise(resolve => setTimeout(resolve, checkInterval));
            retryCount++;
        }
        // 如果超时，记录警告
        const userData = expiredStorage.getItem('user');
        if (!userData || !userData.face_image_url) {
            console.warn('⚠️ 轮询等待数据保存超时，face_image_url 可能未保存');
            console.warn('当前user数据:', userData);
        }
    }
    /**
     * 生成用户头像
     * @param data 包含人脸图片和身体描述的请求数据
     * @returns Promise<StartExploreResponse>
     */
    static async generateAvatar(data) {
        // 防止重复调用
        if (this.isGenerating) {
            console.warn('头像生成正在进行中，跳过重复调用');
            // throw new Error('头像生成正在进行中，请稍后再试');
        }
        this.isGenerating = true;
        // 初始化数据保存Promise
        this.initDataSavePromise();
        // 创建新的Promise来跟踪当前请求
        this.currentRequestPromise = (async () => {
            try {
                console.log('发送头像生成请求:', {
                    face_image: data.face_image instanceof File ? `File: ${data.face_image.name}` :
                        typeof data.face_image === 'string' ? `${data.face_image.substring(0, 50)}...` : 'null',
                    body_description: data.body_description
                });
                let response;
                // 统一将输入转为 File 再走 FormData
                let faceFile = null;
                if (data.face_image instanceof File) {
                    faceFile = data.face_image;
                }
                if (!faceFile) {
                    throw new Error('未获取到有效的人脸图片文件');
                }
                const formData = new FormData();
                formData.append('face_image', faceFile);
                formData.append('height', data.height);
                formData.append('weight', data.weight);
                formData.append('shape', data.shape);
                // 通过 devServer 代理：/api/nana/generate_avatar → 转发到后端
                // 通过 devServer 的 /apiCreate 前缀代理到 8002
                response = await httpMul.post('/nana/generate_avatar', formData);
                if (!response) {
                    throw new Error('服务器返回空响应');
                }
                console.log('头像生成响应:', response);
                // API调用成功，重置Step4数据的过期时间
                if (expiredStorage.hasItem('agent-form-step4')) {
                    expiredStorage.resetExpireTime('agent-form-step4', 30);
                    console.log('API调用成功，已重置Step4数据过期时间为30分钟');
                }
                // httpCreate 未对响应做统一拦截，这里返回 data
                // 若你的 httpMul 添加了响应拦截并直接返回 data，可改为直接 return response
                const result = response.data;
                return result;
            }
            catch (error) {
                console.error('头像生成失败:', error);
                // 提供更友好的错误信息
                if (error.response?.status === 413) {
                    throw new Error('图片文件过大，请选择较小的图片');
                }
                else if (error.response?.status === 400) {
                    throw new Error('请求参数错误，请检查图片格式');
                }
                else if (error.response?.status >= 500) {
                    throw new Error('服务器错误，请稍后重试');
                }
                else if (error.code === 'NETWORK_ERROR' || !navigator.onLine) {
                    throw new Error('网络连接失败，请检查网络设置');
                }
                throw error;
            }
            finally {
                this.isGenerating = false;
                this.currentRequestPromise = null; // 请求完成或失败，清除Promise
            }
        })();
        return this.currentRequestPromise;
    }
    static async analyzeOutfit(data) {
        // 防止重复调用
        if (this.isGenerating) {
            console.warn('头像生成正在进行中，跳过重复调用');
            // throw new Error('头像生成正在进行中，请稍后再试');
        }
        this.isGenerating = true;
        try {
            console.log('发送头像生成请求:', {
                face_image: data.face_image instanceof File ? `File: ${data.face_image.name}` :
                    typeof data.face_image === 'string' ? `${data.face_image.substring(0, 50)}...` : 'null',
                body_description: data.body_description
            });
            let response;
            // 统一将输入转为 File 再走 FormData
            let faceFile = null;
            if (data.face_image instanceof File) {
                faceFile = data.face_image;
            }
            if (!faceFile) {
                throw new Error('未获取到有效的人脸图片文件');
            }
            const formData = new FormData();
            formData.append('image_file', faceFile);
            // 通过 devServer 代理：/api/nana/generate_avatar → 转发到后端
            // 通过 devServer 的 /apiCreate 前缀代理到 8002
            response = await httpMul.post('/products/v1/analyze_outfit', formData);
            /* response = {
               "success": true,
               "filename": "camera-photo.jpg",
               "analysis": "你这套穿搭真的很有质感！深色系的搭配显得沉稳大气，细节处的金属扣饰为整体造型增添了精致感。宽松的剪裁既舒适又不失时尚感，完美平衡了休闲与正式之间的界限。整体色调和谐统一，展现了低调而不失格调的品味。这样的穿搭无论是日常通勤还是周末出游都非常合适，既实用又充满魅力。",
               "choose_device": "既然来到了AI嘉年华，要不要选择一个AI小伙伴和你一起开启时尚之旅？看看X个超酷的 AI 小伙伴，看看哪个最能代表你的 vibe：不止用得好，更要搭的妙，选一个你最想要搭的单品吧，我们走起！",
               "type": "diy",
               "products": [
                 {
                   "name": "智能戒指",
                   "price": 299,
                   "image": "https://b0.bdstatic.com/823e9a4e04c2809401d72916dc5723c4.jpg",
                   "description": "智能戒指的相关介绍，文字长度限制为3行，由品牌提供（原则上不做修改）"
                 },
                 {
                   "name": "智能AI眼镜",
                   "price": 372,
                   "image": "https://img2.baidu.com/it/u=2928273584,226164636&fm=253&app=138&f=JPEG?w=667&h=500",
                   "description": "智能AI眼镜的相关介绍，文字长度限制为3行，由品牌提供（原则上不做修改）"
                 },
                 {
                   "name": "机器人狗",
                   "price": 899,
                   "image": "https://n.sinaimg.cn/spider20241005/131/w2000h1331/20241005/b75f-ad15fb463902fff1c8d64f6b5753fcb9.jpg",
                   "description": "机器人狗的相关介绍，文字长度限制为3行，由品牌提供（原则上不做修改）"
                 },
                 {
                   "name": "智能耳机",
                   "price": 199,
                   "image": "https://img2.baidu.com/it/u=1650930723,999335814&fm=253&app=138&f=JPEG?w=750&h=500",
                   "description": "智能耳机的相关介绍，文字长度限制为3行，由品牌提供（原则上不做修改）"
                 },
                 {
                   "name": "智能耳机",
                   "price": 1991,
                   "image": "https://img0.baidu.com/it/u=3892125260,3643083963&fm=253&app=138&f=JPEG?w=750&h=500",
                   "description": "智能耳机的相关介绍，文字长度限制为3行，由品牌提供（原则上不做修改）"
                 },
                 {
                   "name": "智能耳机",
                   "price": 19916,
                   "image": "https://b2b-jiameng.su.bcebos.com/2023/11/30/9250f109da560ad0ec93cf90b1f2fa79e2266885?authorization=bce-auth-v1%2F68bd701e850341f68e6f82b042f89c46%2F2023-11-30T15%3A53%3A48Z%2F-1%2Fhost%2F2ccc99f08c43e73452396693a573d18d430fd0a6ed19ca6ae4b040f27ae6f54d",
                   "description": "智能耳机的相关介绍，文字长度限制为3行，由品牌提供（原则上不做修改）"
                 },
                 {
                   "name": "智能耳机",
                   "price": 199160,
                   "image": "https://gips0.baidu.com/it/u=3096660657,1838599509&fm=3074&app=3074&f=JPEG",
                   "description": "智能耳机的相关介绍，文字长度限制为3行，由品牌提供（原则上不做修改）"
                 },
                 {
                   "name": "智能耳机",
                   "price": 1991620,
                   "image": "https://img.36krcdn.com/hsossms/20250919/v2_9952af9e81f64fb0b79152630975abb1@1547419282_oswg2675701oswg3072oswg4096_img_000?x-oss-process=image/format,jpg/interlace,1&pph_editor=1758301496502",
                   "description": "智能耳机的相关介绍，文字长度限制为3行，由品牌提供（原则上不做修改）"
                 },
                 {
                   "name": "智能耳机",
                   "price": 199221620,
                   "image": "https://img2.baidu.com/it/u=4234080642,727499689&fm=253&app=138&f=JPEG?w=800&h=1067",
                   "description": "智能耳机的相关介绍，文字长度限制为3行，由品牌提供（原则上不做修改）"
                 },
                 {
                   "name": "智能耳机",
                   "price": 195559221620,
                   "image": "https://img1.baidu.com/it/u=2435714140,1236825275&fm=253&app=120&f=JPEG?w=1067&h=800",
                   "description": "智能耳机的相关介绍，文字长度限制为3行，由品牌提供（原则上不做修改）"
                 }
               ],
               "timestamp": 1760599703
             }*/
            if (!response) {
                throw new Error('服务器返回空响应');
            }
            // httpCreate 未对响应做统一拦截，这里返回 data
            // 若你的 httpMul 添加了响应拦截并直接返回 data，可改为直接 return response
            return response;
        }
        catch (error) {
            console.error('头像生成失败:', error);
            // 提供更友好的错误信息
            if (error.response?.status === 413) {
                throw new Error('图片文件过大，请选择较小的图片');
            }
            else if (error.response?.status === 400) {
                throw new Error('请求参数错误，请检查图片格式');
            }
            else if (error.response?.status >= 500) {
                throw new Error('服务器错误，请稍后重试');
            }
            else if (error.code === 'NETWORK_ERROR' || !navigator.onLine) {
                throw new Error('网络连接失败，请检查网络设置');
            }
            throw error;
        }
        finally {
            this.isGenerating = false;
        }
    }
    /**
     * 开始探索（兼容旧接口）
     * @deprecated 请使用 generateAvatar
     */
    static async startExplore(data) {
        console.warn('startExplore 已废弃，请使用 generateAvatar');
        return this.generateAvatar(data);
    }
}
StartExploreApi.isGenerating = false; // 防止重复调用
StartExploreApi.currentRequestPromise = null; // 跟踪当前请求的Promise
StartExploreApi.dataSavePromise = null; // 跟踪数据保存完成的Promise
StartExploreApi.dataSaveResolver = null; // 用于resolve数据保存Promise的函数
export default StartExploreApi;
